import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useState } from "react";

import type { RulesCapability } from "../rules/ruleTypes";
import { getNativeRulesStatus } from "./rules";

/**
 * Live native app-blocking capability (Usage Access / overlay / Screen Time).
 * Refreshes on mount and when the app returns to foreground (e.g. after Settings).
 */
export function useNativeAppBlockingStatus(): {
  appBlocking: RulesCapability | null;
  refresh: () => Promise<RulesCapability | null>;
  setAppBlocking: (capability: RulesCapability | null) => void;
} {
  const [appBlocking, setAppBlocking] = useState<RulesCapability | null>(null);

  const refresh = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      setAppBlocking(null);
      return null;
    }
    try {
      const status = await getNativeRulesStatus();
      setAppBlocking(status.appBlocking);
      return status.appBlocking;
    } catch {
      setAppBlocking(null);
      return null;
    }
  }, []);

  useEffect(() => {
    void refresh();
    if (!Capacitor.isNativePlatform()) return;
    const listener = App.addListener("appStateChange", (event) => {
      if (event.isActive) void refresh();
    });
    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [refresh]);

  return { appBlocking, refresh, setAppBlocking };
}
